import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Calendar as CalendarIcon, Plus, Trash2, Star, Building, Flag, PartyPopper, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { canUserEdit, canUserDelete } from '@/utils/permissions';

function HRCalendar() {
  const { API, user } = useContext(AppContext);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState('2025');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    type: 'Company',
    description: ''
  });

  const holidayTypes = ['National', 'Festival', 'Bank', 'Company', 'Port'];
  const years = ['2024', '2025', '2026'];
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const canEdit = canUserEdit(user, '/hr/calendar');
  const canDelete = canUserDelete(user, '/hr/calendar');

  useEffect(() => {
    fetchHolidays();
  }, [selectedYear, typeFilter]);

  const fetchHolidays = async () => {
    try {
      let url = `${API}/hr/holidays?year=${selectedYear}`;
      if (typeFilter !== 'all') url += `&type=${typeFilter}`;
      const response = await axios.get(url);
      setHolidays(response.data);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams(formData);
      await axios.post(`${API}/hr/holidays?${params}`, {});
      setShowAddModal(false);
      setFormData({ name: '', date: '', type: 'Company', description: '' });
      fetchHolidays();
    } catch (error) {
      console.error('Error adding holiday:', error);
      alert('Failed to add holiday');
    }
  };

  const handleDeleteHoliday = async (holidayId) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) return;
    try {
      await axios.delete(`${API}/hr/holidays/${holidayId}`);
      fetchHolidays();
    } catch (error) {
      console.error('Error deleting holiday:', error);
      alert('Failed to delete holiday');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'National': return <Flag className="text-orange-500" size={16} />;
      case 'Festival': return <PartyPopper className="text-purple-500" size={16} />;
      case 'Bank': return <Building className="text-blue-500" size={16} />;
      case 'Company': return <Star className="text-green-500" size={16} />;
      case 'Port': return <Star className="text-gray-500" size={16} />;
      default: return <CalendarIcon className="text-gray-500" size={16} />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'National': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Festival': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'Bank': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Company': return 'bg-green-100 text-green-700 border-green-200';
      case 'Port': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  // Build calendar grid
  const buildCalendarDays = () => {
    const year = parseInt(selectedYear);
    const daysInMonth = getDaysInMonth(year, currentMonth);
    const firstDay = getFirstDayOfMonth(year, currentMonth);
    const days = [];

    // Empty cells for days before the 1st
    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null, holidays: [] });
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayHolidays = holidays.filter(h => h.date === dateStr);
      days.push({ day, date: dateStr, holidays: dayHolidays });
    }

    return days;
  };

  const calendarDays = buildCalendarDays();

  // Group holidays by month for list view
  const holidaysByMonth = holidays.reduce((acc, holiday) => {
    const month = new Date(holiday.date).getMonth();
    if (!acc[month]) acc[month] = [];
    acc[month].push(holiday);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a237e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hr-calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarIcon className="text-[#1a237e]" size={28} />
            Holiday Calendar
          </h1>
          <p className="text-gray-600 mt-1">Company holidays and important dates for {selectedYear}</p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593] transition"
            data-testid="add-holiday-btn"
          >
            <Plus size={18} />
            Add Holiday
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{holidays.length}</p>
          <p className="text-sm text-gray-600">Total Holidays</p>
        </div>
        {holidayTypes.map(type => (
          <div key={type} className={`rounded-xl border p-4 text-center ${getTypeColor(type)}`}>
            <p className="text-2xl font-bold">{holidays.filter(h => h.type === type).length}</p>
            <p className="text-sm">{type}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] focus:border-[#1a237e] outline-none"
            data-testid="year-filter"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] focus:border-[#1a237e] outline-none"
            data-testid="type-filter"
          >
            <option value="all">All Types</option>
            {holidayTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Calendar View */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Month Navigation */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-[#1a237e] to-[#283593] text-white">
          <button
            onClick={() => setCurrentMonth(prev => prev === 0 ? 11 : prev - 1)}
            className="p-2 hover:bg-white/20 rounded-lg"
            data-testid="prev-month-btn"
          >
            <ChevronLeft size={20} />
          </button>
          <h3 className="text-lg font-semibold">{months[currentMonth]} {selectedYear}</h3>
          <button
            onClick={() => setCurrentMonth(prev => prev === 11 ? 0 : prev + 1)}
            className="p-2 hover:bg-white/20 rounded-lg"
            data-testid="next-month-btn"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="p-4">
          <div className="grid grid-cols-7 gap-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
                {day}
              </div>
            ))}
            {calendarDays.map((cell, index) => (
              <div
                key={index}
                className={`min-h-[80px] p-2 border border-gray-100 rounded-lg ${
                  cell.day ? 'bg-white' : 'bg-gray-50'
                } ${cell.holidays.length > 0 ? 'bg-red-50 border-red-200' : ''}`}
              >
                {cell.day && (
                  <>
                    <div className={`text-sm font-medium ${cell.holidays.length > 0 ? 'text-red-600' : 'text-gray-700'}`}>
                      {cell.day}
                    </div>
                    {cell.holidays.map(holiday => (
                      <div
                        key={holiday.id}
                        className={`mt-1 text-xs px-1 py-0.5 rounded truncate ${getTypeColor(holiday.type)}`}
                        title={holiday.name}
                      >
                        {holiday.name}
                      </div>
                    ))}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Holiday List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">All Holidays - {selectedYear}</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {holidays.length > 0 ? (
            holidays.map((holiday) => (
              <div
                key={holiday.id}
                className="p-4 flex items-center justify-between hover:bg-gray-50"
                data-testid={`holiday-item-${holiday.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${getTypeColor(holiday.type)}`}>
                    {getTypeIcon(holiday.type)}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{holiday.name}</h4>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span>{new Date(holiday.date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getTypeColor(holiday.type)}`}>{holiday.type}</span>
                    </div>
                    {holiday.description && (
                      <p className="text-sm text-gray-500 mt-1">{holiday.description}</p>
                    )}
                  </div>
                </div>
                {canDelete && (
                  <button
                    onClick={() => handleDeleteHoliday(holiday.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    data-testid={`delete-holiday-${holiday.id}`}
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            ))
          ) : (
            <div className="p-8 text-center">
              <CalendarIcon className="mx-auto text-gray-400 mb-3" size={48} />
              <p className="text-gray-500">No holidays found for the selected filters</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Holiday Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Add Holiday</h2>
              <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddHoliday} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none"
                  placeholder="e.g., Diwali"
                  data-testid="holiday-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none"
                  data-testid="holiday-date-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                <select
                  required
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none"
                  data-testid="holiday-type-select"
                >
                  {holidayTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none resize-none"
                  placeholder="Optional description..."
                  data-testid="holiday-desc-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593]"
                  data-testid="submit-holiday-btn"
                >
                  Add Holiday
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default HRCalendar;
